import classNames from 'classnames';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useHardwareFirstTotals from '../../../../components/HardwareFirstCart/useHardwareFirstTotals';
import { giftPlanRowLabel } from '../../../../components/HardwareFirstGift/giftTermLabelKeys';
import { useCartPricing, useCartPricingError } from '../../../../contexts/CartPricingContext';
import useRequiresShippingDetails from '../../../../hooks/useRequiresShippingDetails';
import CheckoutContext from '../../../../lib/CheckoutContext';
import * as types from '../../../../types';
import styles from './HardwareFirstCheckoutTotals.module.scss';

interface HardwareFirstCheckoutTotalsProps {
  selectedShippingOption?: types.IShippingOption;
}

function Row({ label, value, discount = false }: { label: string; value: string; discount?: boolean }) {
  return (
    <div className={classNames(styles.row, { [styles.discount]: discount })}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
    </div>
  );
}

/**
 * The checkout's receipt: the cart's own figures restated where the card is taken, with shipping
 * and tax added once the address has priced them. The code row carries the code itself.
 */
export default function HardwareFirstCheckoutTotals({ selectedShippingOption }: HardwareFirstCheckoutTotalsProps) {
  const { t } = useTranslation();
  const { cart } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const pricingError = useCartPricingError();
  const requiresShippingDetails = useRequiresShippingDetails();
  const {
    trackerCount,
    hardwareInCents,
    trialInCents,
    promoDiscountInCents,
    multiDeviceDiscountInCents,
    giftPlanInCents,
    giftTermMonths,
    formatPrice,
  } = useHardwareFirstTotals();
  const taxInCents = cartPricing.taxInCents ?? 0;
  const money = (cents: number) => (pricingError ? '—' : formatPrice(cents));
  const minus = (cents: number) => t('negative_amount', { amount: money(cents) });

  return (
    <div className={styles.totals}>
      <div className={styles.divider} />
      <Row label={t('hardware_first_checkout_hardware')} value={money(hardwareInCents)} />
      {trialInCents > 0 && <Row discount label={t('hardware_first_cart_on_trial')} value={minus(trialInCents)} />}
      {promoDiscountInCents > 0 && (
        <Row discount label={cart.couponCode ?? t('discount')} value={minus(promoDiscountInCents)} />
      )}
      {multiDeviceDiscountInCents > 0 && (
        <Row
          discount
          label={t('hardware_first_cart_multi_device_discount')}
          value={minus(multiDeviceDiscountInCents)}
        />
      )}
      {giftTermMonths && (
        <Row label={giftPlanRowLabel(t, giftTermMonths, trackerCount)} value={money(giftPlanInCents)} />
      )}
      {requiresShippingDetails && !!selectedShippingOption && (
        <Row
          label={t('shipping')}
          value={cartPricing.shippingInCents ? money(cartPricing.shippingInCents) : t('free')}
        />
      )}
      {taxInCents > 0 && <Row label={t('tax')} value={money(taxInCents)} />}
      <div className={styles.divider} />
      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>{t('total')}</span>
        <span className={styles.totalValue}>{money(cartPricing.totalInCents)}</span>
      </div>
    </div>
  );
}
